import { forwardRef, ButtonHTMLAttributes } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-gradient-to-r from-ocean-cyan to-ocean-blue',
    'text-white font-semibold',
    'shadow-lg shadow-ocean-cyan/25',
    'hover:shadow-xl hover:shadow-ocean-cyan/30',
    'hover:brightness-110',
    'active:brightness-95',
    'border border-ocean-cyan/20'
  ),
  secondary: cn(
    'bg-white/5 backdrop-blur-sm',
    'text-gray-200',
    'border border-white/10',
    'hover:bg-white/10 hover:border-white/20',
    'active:bg-white/5'
  ),
  ghost: cn(
    'bg-transparent',
    'text-gray-300',
    'hover:bg-white/5 hover:text-white',
    'active:bg-white/10'
  ),
  danger: cn(
    'bg-signal-error/20 backdrop-blur-sm',
    'text-signal-error',
    'border border-signal-error/30',
    'hover:bg-signal-error/30 hover:border-signal-error/50',
    'active:bg-signal-error/20'
  ),
  success: cn(
    'bg-signal-success/20 backdrop-blur-sm',
    'text-signal-success',
    'border border-signal-success/30',
    'hover:bg-signal-success/30 hover:border-signal-success/50',
    'active:bg-signal-success/20'
  ),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-lg gap-1.5',
  md: 'px-5 py-2.5 text-sm rounded-xl gap-2',
  lg: 'px-6 py-3 text-base rounded-xl gap-2.5',
  icon: 'p-2.5 rounded-xl',
};

/**
 * Button - Animated button component with multiple variants
 * 
 * Features:
 * - Multiple variants (primary, secondary, ghost, danger, success)
 * - Size options (sm, md, lg, icon)
 * - Loading state with spinner
 * - Left/right icon support
 * - Framer Motion animations
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <motion.button
        ref={ref}
        whileHover={!isDisabled ? { scale: 1.02 } : undefined}
        whileTap={!isDisabled ? { scale: 0.98 } : undefined}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className={cn(
          // Base styles
          'relative inline-flex items-center justify-center',
          'font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50 focus:ring-offset-2 focus:ring-offset-navy-900',
          // Variant & Size
          variantStyles[variant],
          sizeStyles[size],
          // States
          fullWidth && 'w-full',
          isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {/* Loading spinner */}
        {isLoading && (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        )}

        {/* Left icon */}
        {!isLoading && leftIcon && (
          <span className="flex-shrink-0">{leftIcon as React.ReactNode}</span>
        )}

        {/* Content */}
        {size !== 'icon' && <span>{children as React.ReactNode}</span>}
        {size === 'icon' && (children as React.ReactNode)}

        {/* Right icon */}
        {!isLoading && rightIcon && (
          <span className="flex-shrink-0">{rightIcon as React.ReactNode}</span>
        )}

        {/* Shine effect for primary variant */}
        {variant === 'primary' && !isDisabled && (
          <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
            <div 
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)',
              }}
            />
          </div>
        )}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

